/**
 * vaultRecovery — devolve ao servidor o que ficou preso no cofre local.
 *
 * Roda no boot do portal (e a cada 5min): procura sessões de gravação cujos
 * pedaços ficaram no IndexedDB porque a rede caiu e a aba morreu antes de
 * subir. Sobe os pedaços pro MESMO prefixo do Storage e manda o servidor
 * re-montar a gravação inteira por cima da parcial (re-recover).
 *
 * É a metade que faltou no caso Sandra (31/08): o cofre guarda; isto entrega.
 *
 * TRÊS GUARDAS, e a razão de cada uma:
 * - lastWriteIso < 2min atrás → PULA: pode ser gravação VIVA (nesta ou noutra
 *   aba do mesmo perfil). Recuperar no meio seria disputar com o gravador.
 * - mais velho que VAULT_MAX_AGE_DAYS → descarta sem subir: consulta de duas
 *   semanas atrás já foi resolvida por outros meios; subir agora só confundiria
 *   a ficha com um "áudio novo" antigo.
 * - o re-recover é chamado NA SEQUÊNCIA do upload, nunca "depois": o GC do
 *   servidor apaga chunks de sessão encerrada em minutos, e o upload reinicia
 *   esse relógio — a janela existe enquanto a gente age imediatamente.
 */

import { supabase } from './supabaseClient';
import { getWebhookUrl } from '../hooks/admin/useAudioRecorder';
import {
  vaultListSessions,
  vaultGetChunks,
  vaultDropSession,
  vaultDeleteChunk,
  VAULT_MAX_AGE_DAYS,
} from './recordingVault';
import { webhookFetch } from './webhookFetch';

const LIVE_GRACE_MS = 2 * 60 * 1000;

let running = false;

export async function runVaultRecovery(): Promise<void> {
  if (running) return;
  running = true;
  try {
    const sessions = await vaultListSessions();
    for (const meta of sessions) {
      try {
        const idadeUltimaEscrita = Date.now() - new Date(meta.lastWriteIso).getTime();
        if (idadeUltimaEscrita < LIVE_GRACE_MS) continue; // pode estar gravando AGORA

        const idadeSessao = Date.now() - new Date(meta.startTimeIso).getTime();
        if (idadeSessao > VAULT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000) {
          await vaultDropSession(meta.sessionId); // fóssil
          continue;
        }

        const chunks = await vaultGetChunks(meta.sessionId);
        if (chunks.length === 0) {
          await vaultDropSession(meta.sessionId); // tudo já confirmado antes de morrer
          continue;
        }

        console.log(
          `[vaultRecovery] sessão órfã ${meta.sessionId} (${meta.patientName}): ` +
          `${chunks.length} pedaços pendentes — enviando`,
        );

        // Sobe cada pedaço pro MESMO caminho que o gravador usaria (upsert:
        // re-executar é inofensivo). Só apaga do cofre o que o servidor aceitar.
        let enviados = 0;
        let maiorIndex = 0;
        for (const { index, blob } of chunks) {
          const path = `${meta.storagePrefix}/chunk-${String(index).padStart(4, '0')}.webm`;
          const { error } = await supabase.storage
            .from('consultation-audio')
            .upload(path, blob, { contentType: 'audio/webm', upsert: true });
          if (error) throw new Error(`chunk ${index}: ${error.message}`);
          await vaultDeleteChunk(meta.sessionId, index);
          enviados++;
          maiorIndex = Math.max(maiorIndex, index);
        }

        // Atualiza o contador SEM tocar last_chunk_at: a guarda do servidor
        // (recoverSessionNow recusa last_chunk <30s) leria um last_chunk_at
        // recém-escrito como "gravação ativa" e devolveria 409 ao re-recover
        // logo abaixo — anulando o caminho imediato E atrasando o cron de
        // abandono em +10min. O last_chunk_at REAL (da gravação morta, ≥2min
        // pela guarda LIVE_GRACE_MS) é exatamente o que deixa os dois caminhos
        // funcionarem. GC não é risco: só roda em sessão encerrada.
        await supabase
          .from('recording_sessions')
          .update({
            chunk_count: maiorIndex + 1,
            updated_at: new Date().toISOString(),
          })
          .eq('id', meta.sessionId);

        // IMEDIATAMENTE: servidor re-monta o áudio inteiro sobre a parcial
        const res = await webhookFetch(`${getWebhookUrl()}/api/recording/re-recover`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: meta.sessionId }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          // 409 "nada a re-recuperar"/"sem ganho" não é falha de entrega: os
          // pedaços estão no Storage e o cofre já esvaziou. Só loga.
          console.warn(`[vaultRecovery] re-recover ${meta.sessionId}:`, body?.error || res.status);
        } else {
          console.log(`[vaultRecovery] sessão ${meta.sessionId} re-montada (${enviados} pedaços entregues)`);
        }
        await vaultDropSession(meta.sessionId);
      } catch (err: any) {
        // Falhou no meio (rede de novo?): o que não subiu CONTINUA no cofre —
        // a próxima varredura recomeça de onde parou.
        console.warn(`[vaultRecovery] sessão ${meta.sessionId} adiada:`, err?.message);
      }
    }
  } finally {
    running = false;
  }
}

/** Liga a varredura: uma no boot (após respiro) e depois a cada 5min. */
export function startVaultRecovery(): () => void {
  const t = setTimeout(() => runVaultRecovery(), 8_000);
  const iv = setInterval(() => runVaultRecovery(), 5 * 60 * 1000);
  return () => {
    clearTimeout(t);
    clearInterval(iv);
  };
}
